import {
  Button,
  Checkbox,
  Drawer,
  Form,
  Input,
  Radio,
  Select,
  Spin,
  Table,
} from 'antd'
import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  useFetchLaboratoriesListQuery,
  useFetchModulesListQuery,
  useFetchSurveyCompaniesListQuery,
  useFetchUserRolesListQuery,
} from 'domain/User/userApi'
import { DeleteOutlined } from '@ant-design/icons'
import PropTypes from 'prop-types'
import userRoles from 'constants/userRoles'
import { useTranslation } from 'react-i18next'
import vesselDomain from 'domain/Vessel'

import './CreateUserDrawer.less'

const { Option } = Select

const CreateUserDrawer = ({
  editData,
  loading,
  onClose,
  onResetPassword,
  onSubmit,
  open,
  resettingPassword,
  submitError,
  submitting,
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const vessels = useSelector(vesselDomain.selectors.allVesselsSelector)
  const { data: rolesList = [] } = useFetchUserRolesListQuery()
  const { data: modulesList = [] } = useFetchModulesListQuery()
  const { data: laboratoriesList = [] } = useFetchLaboratoriesListQuery()
  const { data: surveyCompaniesList = [] } = useFetchSurveyCompaniesListQuery()
  const [form] = Form.useForm()
  const selectedRole = Form.useWatch('role', form)
  const [selectedVessels, setSelectedVessels] = useState([])

  const vesselNameByImo = useMemo(
    () =>
      (Array.isArray(vessels) ? vessels : []).reduce((acc, vessel) => {
        acc[String(vessel.imoNumber)] = vessel.vesselName

        return acc
      }, {}),
    [vessels]
  )

  const moduleOptions = modulesList.map(m => ({
    label: m.name,
    value: m.id,
  }))

  const handleVesselListChange = values => {
    setSelectedVessels(values)
    form.setFieldsValue({ vesselList: values })
  }

  const handleRemoveVessel = vesselId => {
    handleVesselListChange(selectedVessels.filter(id => id !== vesselId))
  }

  useEffect(() => {
    if (open) {
      dispatch(vesselDomain.actions.requestVessels(1, 1000, ''))
    }
  }, [open, dispatch])

  useEffect(() => {
    if (open) {
      if (editData) {
        const normalizedVesselList = Array.isArray(editData.vesselList)
          ? editData.vesselList.map(imoNumber => String(imoNumber))
          : []

        form.setFieldsValue({
          ...editData,
          vessel: editData.vessel ? String(editData.vessel) : editData.vessel,
          vesselList: normalizedVesselList,
        })
        setSelectedVessels(normalizedVesselList)
      } else {
        form.resetFields()
        setSelectedVessels([])
      }
    }
  }, [open, editData, form])

  const handleClose = () => {
    form.resetFields()
    onClose()
  }

  const handleFinish = values => {
    onSubmit(values)
  }

  const isShipRole = selectedRole === userRoles.SHIP
  const isAdminRole = selectedRole === userRoles.ADMIN
  const isVesselManagementRole = selectedRole === userRoles.VESSEL_MANAGEMENT
  const isSurveyorRole = selectedRole === userRoles.SURVEYOR
  const isLabRole = selectedRole === userRoles.LAB
  const showResetPasswordButton =
    Boolean(editData) && (isAdminRole || isShipRole)

  return (
    <Drawer
      destroyOnClose
      onClose={handleClose}
      open={open}
      width={480}
      footer={
        <div className="create-user-drawer__footer">
          <div className="create-user-drawer__footer-row">
            <div>
              {showResetPasswordButton ? (
                <Button
                  className="create-user-drawer__reset-password"
                  loading={resettingPassword}
                  onClick={onResetPassword}
                >
                  {t('admin.userManagement.drawer.resetPassword')}
                </Button>
              ) : null}
            </div>
            <div className="create-user-drawer__actions">
              <Button
                className="create-user-drawer__cancel"
                onClick={handleClose}
              >
                {t('admin.userManagement.drawer.cancel')}
              </Button>
              <Button
                loading={submitting}
                onClick={() => form.submit()}
                type="primary"
              >
                {editData
                  ? t('admin.userManagement.drawer.save')
                  : t('admin.userManagement.drawer.create')}
              </Button>
            </div>
          </div>
          {submitError ? (
            <div className="admin-drawer-error-message">
              <span>{submitError}</span>
            </div>
          ) : null}
        </div>
      }
      title={
        editData
          ? t('admin.userManagement.drawer.editTitle')
          : t('admin.userManagement.drawer.title')
      }
    >
      <Spin spinning={loading}>
        <Form
          form={form}
          initialValues={{ status: 'active' }}
          layout="vertical"
          onFinish={handleFinish}
        >
          {/* Username / Email */}
          <Form.Item
            label={t('admin.userManagement.drawer.fields.username')}
            name="username"
            rules={[
              {
                message: t(
                  'admin.userManagement.drawer.validation.usernameRequired'
                ),
                required: true,
              },
              {
                message: t(
                  'admin.userManagement.drawer.validation.usernameInvalid'
                ),
                pattern: /^[a-zA-Z0-9._%+\-@!#$&*]+$/,
              },
            ]}
          >
            <Input
              disabled={Boolean(editData)}
              placeholder={t(
                'admin.userManagement.drawer.placeholders.username'
              )}
            />
          </Form.Item>

          <Form.Item
            label={t('admin.userManagement.drawer.fields.email')}
            name="email"
            rules={[
              {
                message: t(
                  'admin.userManagement.drawer.validation.emailRequired'
                ),
                required: true,
              },
              {
                message: t(
                  'admin.userManagement.drawer.validation.emailInvalid'
                ),
                type: 'email',
              },
            ]}
          >
            <Input placeholder="Enter email" />
          </Form.Item>

          {/* Role */}
          <Form.Item
            label={t('admin.userManagement.drawer.fields.role')}
            name="role"
            rules={[
              {
                message: t(
                  'admin.userManagement.drawer.validation.roleRequired'
                ),
                required: true,
              },
            ]}
          >
            <Select
              placeholder={t('admin.userManagement.drawer.placeholders.role')}
            >
              {rolesList.map(roleItem => (
                <Option key={roleItem.id} value={roleItem.role.toLowerCase()}>
                  {roleItem.role}
                </Option>
              ))}
            </Select>
          </Form.Item>

          {/* Module */}
          <Form.Item
            label={t('admin.userManagement.drawer.fields.module')}
            name="module"
            rules={[
              {
                message: t(
                  'admin.userManagement.drawer.validation.moduleRequired'
                ),
                required: true,
              },
            ]}
          >
            <Checkbox.Group options={moduleOptions} />
          </Form.Item>

          {/* Status */}
          <Form.Item
            label={t('admin.userManagement.drawer.fields.status')}
            name="status"
            rules={[
              {
                message: t(
                  'admin.userManagement.drawer.validation.statusRequired'
                ),
                required: true,
              },
            ]}
          >
            <Radio.Group>
              <Radio value="active">
                {t('admin.userManagement.table.active')}
              </Radio>
              <Radio value="inactive">
                {t('admin.userManagement.table.inactive')}
              </Radio>
            </Radio.Group>
          </Form.Item>

          {/* Vessel — only for Ship role */}
          {isShipRole && (
            <Form.Item
              label={t('admin.userManagement.drawer.fields.vessel')}
              name="vessel"
              rules={[
                {
                  message: t(
                    'admin.userManagement.drawer.validation.vesselRequired'
                  ),
                  required: true,
                },
              ]}
            >
              <Select
                showSearch
                filterOption={(input, option) =>
                  option?.children?.toLowerCase().includes(input.toLowerCase())
                }
                placeholder={t(
                  'admin.userManagement.drawer.placeholders.vessel'
                )}
              >
                {vessels.map(v => (
                  <Option key={v.imoNumber} value={String(v.imoNumber)}>
                    {v.vesselName}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}

          {/* Vessel List — only for Vessel Management role */}
          {isVesselManagementRole && (
            <>
              {/* Hidden field keeps form store in sync */}
              <Form.Item
                hidden
                name="vesselList"
                rules={[
                  {
                    message: t(
                      'admin.userManagement.drawer.validation.vesselListRequired'
                    ),
                    min: 1,
                    required: true,
                    type: 'array',
                  },
                ]}
              >
                <Select mode="multiple" />
              </Form.Item>

              <Form.Item noStyle shouldUpdate>
                {() => {
                  const errors = form.getFieldError('vesselList')

                  return (
                    <Form.Item
                      help={errors[0] || null}
                      label={t('admin.userManagement.drawer.fields.vesselList')}
                      required
                      validateStatus={errors.length > 0 ? 'error' : ''}
                    >
                      <Select
                        className="create-user-drawer__vessels-select"
                        mode="multiple"
                        onChange={handleVesselListChange}
                        showSearch
                        value={selectedVessels}
                        filterOption={(input, option) =>
                          option?.children
                            ?.toLowerCase()
                            .includes(input.toLowerCase())
                        }
                        placeholder={t(
                          'admin.userManagement.drawer.placeholders.vesselList'
                        )}
                      >
                        {vessels.map(v => (
                          <Option key={v.imoNumber} value={String(v.imoNumber)}>
                            {v.vesselName}
                          </Option>
                        ))}
                      </Select>

                      {selectedVessels.length > 0 && (
                        <div className="create-user-drawer__selected-table">
                          <Table
                            pagination={false}
                            showHeader={false}
                            size="small"
                            columns={[
                              {
                                dataIndex: 'name',
                                key: 'name',
                              },
                              {
                                align: 'right',
                                key: 'action',
                                render: (_, record) => (
                                  <Button
                                    danger
                                    icon={<DeleteOutlined />}
                                    size="small"
                                    type="text"
                                    onClick={() =>
                                      handleRemoveVessel(record.key)
                                    }
                                  />
                                ),
                              },
                            ]}
                            dataSource={[...selectedVessels]
                              .reverse()
                              .map(imoNumber => ({
                                key: imoNumber,
                                name:
                                  vesselNameByImo[String(imoNumber)] ||
                                  'Vessel unavailable',
                              }))}
                          />
                        </div>
                      )}
                    </Form.Item>
                  )
                }}
              </Form.Item>
            </>
          )}

          {/* Survey Company — only for Surveyor role */}
          {isSurveyorRole && (
            <Form.Item
              label={t('admin.userManagement.drawer.fields.surveyCompany')}
              name="surveyCompany"
              rules={[
                {
                  message: t(
                    'admin.userManagement.drawer.validation.surveyCompanyRequired'
                  ),
                  required: true,
                },
              ]}
            >
              <Select
                showSearch
                filterOption={(input, option) =>
                  option?.children?.toLowerCase().includes(input.toLowerCase())
                }
                placeholder={t(
                  'admin.userManagement.drawer.placeholders.surveyCompany'
                )}
              >
                {surveyCompaniesList.map(sc => (
                  <Option key={sc.id} value={sc.id}>
                    {sc.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}

          {/* Laboratory — only for Lab role */}
          {isLabRole && (
            <Form.Item
              label={t('admin.userManagement.drawer.fields.laboratory')}
              name="laboratory"
              rules={[
                {
                  message: t(
                    'admin.userManagement.drawer.validation.laboratoryRequired'
                  ),
                  required: true,
                },
              ]}
            >
              <Select
                showSearch
                filterOption={(input, option) =>
                  option?.children?.toLowerCase().includes(input.toLowerCase())
                }
                placeholder={t(
                  'admin.userManagement.drawer.placeholders.laboratory'
                )}
              >
                {laboratoriesList.map(lab => (
                  <Option key={lab.id} value={lab.id}>
                    {lab.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}
        </Form>
      </Spin>
    </Drawer>
  )
}

CreateUserDrawer.defaultProps = {
  editData: null,
  loading: false,
  onResetPassword: () => {},
  resettingPassword: false,
  submitError: '',
  submitting: false,
}

CreateUserDrawer.propTypes = {
  editData: PropTypes.object,
  loading: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onResetPassword: PropTypes.func,
  onSubmit: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  resettingPassword: PropTypes.bool,
  submitError: PropTypes.string,
  submitting: PropTypes.bool,
}

export default CreateUserDrawer
